/**
 * Shared ship-icon helper — single source of truth for choosing a vessel's
 * map icon across every map (home, FMS, voyage, windy).
 *
 * The icon style is a global, user-controlled preference kept in localStorage:
 *  - 'special' (default): tug boat / landing craft render as a detailed vessel
 *    silhouette with a separate rotating heading cone.
 *  - 'simple': every vessel renders as the plain status-coloured ship marker
 *    that rotates to follow its GPS track.
 *
 * Vessel types without a dedicated silhouette always fall back to the plain
 * marker, so the choice simply has no visible effect for them.
 */
import shipOnline from '@/assets/images/map/online-ship.svg';
import shipOffline from '@/assets/images/map/offline-ship.svg';
import shipNoGps from '@/assets/images/map/no-gps-ship.svg';
import shipNoSensor from '@/assets/images/map/no-sensor-ship.svg';
import markerOnline from '@/assets/images/map/online-marker.svg';
import markerOffline from '@/assets/images/map/offline-marker.svg';
import markerNoGps from '@/assets/images/map/no-gps-marker.svg';
import markerNoSensor from '@/assets/images/map/no-sensor-marker.svg';
import headingOnline from '@/assets/images/map/online-heading.svg';
import headingOffline from '@/assets/images/map/offline-heading.svg';
import headingNoGps from '@/assets/images/map/no-gps-heading.svg';
import headingNoSensor from '@/assets/images/map/no-sensor-heading.svg';
import TugBoatIcon from '@/assets/images/map/tug-boat-icon.svg';
import LandingCraftTransportIcon from '@/assets/images/map/landing-craft-transport-icon.svg';

export const ICON_STYLE_KEY = 'mapIconStyle';
export const ICON_STYLE_SPECIAL = 'special';
export const ICON_STYLE_SIMPLE = 'simple';

const SHIP_BY_STATUS = {
  online: shipOnline,
  offline: shipOffline,
  'no-gps': shipNoGps,
  'no-sensor': shipNoSensor,
};

const MARKER_BY_STATUS = {
  online: markerOnline,
  offline: markerOffline,
  'no-gps': markerNoGps,
  'no-sensor': markerNoSensor,
};

const HEADING_BY_STATUS = {
  online: headingOnline,
  offline: headingOffline,
  'no-gps': headingNoGps,
  'no-sensor': headingNoSensor,
};

// Status asing jatuh ke heading offline.
export function headingIconUrlFor(status) {
  return HEADING_BY_STATUS[status] || headingOffline;
}

// Vessel types that ship with a dedicated detailed silhouette.
const SPECIAL_ICON_BY_TYPE = {
  'tug boat': { iconUrl: TugBoatIcon, iconSize: [60, 86] },
  'landing craft transport': { iconUrl: LandingCraftTransportIcon, iconSize: [70, 86] },
};

const DEFAULT_SHIP_ICON_SIZE = [40, 56];

/**
 * Read the global icon-style preference. Defaults to 'special' so existing
 * behaviour is preserved when nothing has been chosen yet.
 * @returns {'special' | 'simple'}
 */
export function getMapIconStyle() {
  const value = typeof localStorage !== 'undefined' ? localStorage.getItem(ICON_STYLE_KEY) : null;
  return value === ICON_STYLE_SIMPLE ? ICON_STYLE_SIMPLE : ICON_STYLE_SPECIAL;
}

/**
 * Persist the global icon-style preference.
 * @param {'special' | 'simple'} style
 * @returns {'special' | 'simple'} The normalized value that was stored.
 */
export function setMapIconStyle(style) {
  const normalized = style === ICON_STYLE_SIMPLE ? ICON_STYLE_SIMPLE : ICON_STYLE_SPECIAL;
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(ICON_STYLE_KEY, normalized);
  }
  return normalized;
}

export const ICON_SCALE_KEY = 'mapIconScale';
export const MIN_ICON_SCALE = 0.6;
export const MAX_ICON_SCALE = 1.8;
export const DEFAULT_ICON_SCALE = 1;

// Base (100%) sizes for the dot-trail marker and heading cone overlays.
export const MARKER_ICON_BASE_SIZE = [35, 61];
export const HEADING_ICON_BASE_SIZE = [120, 146];

function clampScale(scale) {
  const value = Number(scale);
  if (Number.isNaN(value)) return DEFAULT_ICON_SCALE;
  return Math.min(MAX_ICON_SCALE, Math.max(MIN_ICON_SCALE, value));
}

/**
 * Read the global icon size scale. Defaults to 1 (100%).
 * @returns {number}
 */
export function getMapIconScale() {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(ICON_SCALE_KEY) : null;
  return raw === null ? DEFAULT_ICON_SCALE : clampScale(raw);
}

/**
 * Persist the global icon size scale (clamped to the allowed range).
 * @param {number} scale
 * @returns {number} The normalized value that was stored.
 */
export function setMapIconScale(scale) {
  const normalized = clampScale(scale);
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(ICON_SCALE_KEY, String(normalized));
  }
  return normalized;
}

/**
 * Scale a base [width, height] icon size by the given (or stored) scale.
 * @param {number[]} size - Base [width, height].
 * @param {number} [scale] - Defaults to the stored preference.
 * @returns {number[]} Scaled [width, height] rounded to whole pixels.
 */
export function scaleIconSize(size, scale = getMapIconScale()) {
  return [Math.round(size[0] * scale), Math.round(size[1] * scale)];
}

function normalizeAssetType(asset) {
  return asset?.massMaty?.matyName?.trim()?.toLowerCase() || '';
}

/**
 * Build the icon configuration for an asset given its status and icon style.
 * @param {object} asset - Asset object (uses massMaty.matyName).
 * @param {'online'|'offline'|'no-gps'|'no-sensor'} status - Ship status.
 * @param {'special'|'simple'} [style] - Defaults to the stored preference.
 * @returns {{iconConfig: object, markerIconUrl: string, headingIconUrl: string}}
 */
export function buildIconConfig(asset, status, style = getMapIconStyle(), scale = getMapIconScale()) {
  const special = SPECIAL_ICON_BY_TYPE[normalizeAssetType(asset)];

  let iconUrl;
  let baseSize;
  let hasHeadingIcon;
  if (style === ICON_STYLE_SPECIAL && special) {
    iconUrl = special.iconUrl;
    baseSize = special.iconSize;
    hasHeadingIcon = true;
  } else {
    iconUrl = SHIP_BY_STATUS[status] || shipOffline;
    baseSize = DEFAULT_SHIP_ICON_SIZE;
    hasHeadingIcon = false;
  }

  return {
    iconConfig: {
      iconUrl,
      iconSize: scaleIconSize(baseSize, scale),
      hasHeadingIcon,
    },
    markerIconUrl: MARKER_BY_STATUS[status] || markerOffline,
    headingIconUrl: headingIconUrlFor(status),
  };
}
